import {
  BarElement, CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale, Tooltip
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { formatNumberWithSeparators } from 'common/utils/functions';
import KioskChartLegends from 'extends/KioskChartLegends';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels,
);

const BAR_PERCENTAGE = 0.4;
const CATEGORY_PERCENTAGE = 0.9;

interface Props {
  data?: any,
  options?: ChartOptions<'bar'>
  legends?: { label: string, color: string }[]
  style?: React.CSSProperties
  title?: string
  dataOnTop?: number[]
}

const KioskKidneyRenalByRisk = ({
  data,
  dataOnTop,
  title,
  options: customOptions,
  legends: customLegends,
  style
}: Props) => {
  const { t } = useTranslation();

  const chartRef = useRef<any>(null);
  const [hiddens, setHiddens] = useState<boolean[]>(data?.labels?.map(() => false) || []);

  const legendsData = customLegends || [
    {
      label: t('dashboard2025.haveRenalIllness'),
      color: '#21453F'
    },
    {
      label: t('dashboard2025.haveRenalRisk'),
      color: '#52AD9C'
    },
    {
      label: t('dashboard2025.haveNoRenalRisk'),
      color: '#91CAC0'
    },
  ];

  const dummyData = {
    labels: [
      t('dashboard2025.dtd'),
      t('dashboard2025.tha'),
      t('dashboard2025.rllp'),
    ],
    datasets: [
      // {
      //   data: [10, 35, 55],
      //   backgroundColor: legendsData[0].color,
      //   barPercentage: BAR_PERCENTAGE,
      //   categoryPercentage: CATEGORY_PERCENTAGE,
      // },
      // {
      //   data: [20, 50, 25],
      //   backgroundColor: legendsData[1].color,
      //   barPercentage: BAR_PERCENTAGE,
      //   categoryPercentage: CATEGORY_PERCENTAGE,
      // },
      // {
      //   data: [70, 15, 20],
      //   backgroundColor: legendsData[2].color,
      //   barPercentage: BAR_PERCENTAGE,
      //   categoryPercentage: CATEGORY_PERCENTAGE,
      // },
    ],
  };

  const transformedData = useMemo(() => {
    if (!data) {
      return dummyData;
    }
    const result = {
      ...dummyData,
      ...data,
      datasets: data.datasets.map((dataset: any, index: number) => ({
        ...dataset,
        backgroundColor: legendsData[index % legendsData.length].color,
        barPercentage: BAR_PERCENTAGE,
        categoryPercentage: CATEGORY_PERCENTAGE,
      })),
    };
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 50
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        labels: {
          top: {
            textAlign: 'center',
            anchor: 'end', // attach to top of bar
            align: 'top',
            formatter: (value: number, ctx) => {
              const { datasetIndex } = ctx;
              const totalDatasets = ctx.chart.data.datasets.length;

              // only show for the top-most dataset
              if (datasetIndex === totalDatasets - 1) {
                const { dataIndex } = ctx;
                return (dataOnTop?.[dataIndex] || 0) > 0
                  ? `${formatNumberWithSeparators(dataOnTop?.[dataIndex])}\n${t('dashboard2025.patient')}` : '';
              }
              return '';
            },
            font: {
              weight: 'bold',
              size: 16,
            },
            color: '#111',
          },
          inside: {
            color: 'white',
            anchor: 'center',
            align: 'center',
            formatter: (value: number) => (Number(value) > 2 ? `${value}%` : ''), // show % in each block
            font: {
              weight: 'bold' as const,
              size: 16
            },
          },
        }
      },
      tooltip: {
        callbacks: {
          // Custom label for each dataset
          label: (context: any) => {
            const { datasetIndex } = context;
            const value = context.raw; // raw data value
            return `${(legendsData)[datasetIndex].label}: ${value}%`; // e.g. "Part A: 35%"
          },
        },
      }
    },
    scales: {
      x: {
        type: 'category',
        stacked: true,
        offset: true,
        ticks: {
          color: '#667085',
          align: 'center',
          crossAlign: 'near',
          padding: 16,
          font: {
            weight: 'bold',
            size: 16
          }
        },
        grid: {
          drawTicks: false,
          drawOnChartArea: false
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: 100,
        ticks: {
          color: '#667085',
          callback: (value: string | number) => `${value}%`,
          font: { weight: 'bold', size: 16 },
          padding: 10
        },
        grid: {
          drawBorder: false,
          drawTicks: false,
          borderDash: [4, 6],
        }
      },
    },
    ...customOptions
  };

  const handleLegendClick = (index: number) => {
    const chart = chartRef.current;

    if (!chart) return;

    // Toggle dataset visibility
    const meta = chart.getDatasetMeta(index);
    meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
    chart.update();

    // Update local state (for styling the legend)
    setHiddens((prev) => {
      const newHidden = [...prev];
      newHidden[index] = !newHidden[index];
      return newHidden;
    });
  };

  return (
    <div
      className="p-home_chart"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px',
        ...style
      }}
    >
      <h1
        style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#3D424F',
          marginBottom: '24px',
          width: '100%'
        }}
      >
        {title}
      </h1>
      <div style={{
        width: '100%',
        height: '100%',
      }}
      >
        <Bar ref={chartRef} data={transformedData} options={options} />
      </div>
      <KioskChartLegends
        labels={(legendsData).map((item) => item.label)}
        colors={(legendsData).map((item) => item.color)}
        style={{ marginTop: 10 }}
        onClick={handleLegendClick}
        hiddens={hiddens}
      />
    </div>
  );
};

export default KioskKidneyRenalByRisk;
